import { useState } from 'react';
import { T } from 'gt-react';
import { MicrosoftExcelLogoIcon, TableIcon } from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import {
  isOfficeViewerSupported,
  useIsOfficeViewerEnabled,
  OfficeDocViewer,
} from '@/components/common/OfficeDocViewer';
import { XlsxViewer } from './XlsxViewer';

interface ExcelDocViewerProps {
  /** S3 URL for the local XLSX parser */
  url: string;
  fileName: string;
  /** Document ID for the Office Online viewer (omit to disable the toggle) */
  documentId?: string;
  height?: string | number;
}

type ViewerMode = 'local' | 'office';

/**
 * Excel document viewer with an optional toggle to switch between the
 * local XLSX parser and Microsoft Office Online viewer.
 *
 * The Office Online toggle only appears when:
 * - The `msft-office-viewer-enabled` feature flag is on
 * - A `documentId` is provided
 * - The file extension is supported by Office Online
 */
export function ExcelDocViewer({
  url,
  fileName,
  documentId,
  height = 450,
}: ExcelDocViewerProps) {
  const officeViewerEnabled = useIsOfficeViewerEnabled();
  const canUseOfficeViewer =
    officeViewerEnabled && !!documentId && isOfficeViewerSupported(fileName);

  const [mode, setMode] = useState<ViewerMode>('office');

  if (!canUseOfficeViewer) {
    return <XlsxViewer url={url} fileName={fileName} height={height} />;
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-end gap-1 pb-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={mode === 'local' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setMode('local')}
            >
              <TableIcon className="h-3.5 w-3.5 mr-1" />
              <T>Preview</T>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <T>View with built-in spreadsheet parser</T>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={mode === 'office' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setMode('office')}
            >
              <MicrosoftExcelLogoIcon className="h-3.5 w-3.5 mr-1" />
              <T>Office</T>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <T>View with Microsoft Office Online viewer</T>
          </TooltipContent>
        </Tooltip>
      </div>
      {mode === 'local' ? (
        <XlsxViewer url={url} fileName={fileName} height={height} />
      ) : (
        <OfficeDocViewer
          documentId={documentId}
          height={height}
          className="rounded-md border"
        />
      )}
    </div>
  );
}
